import { NgClass } from '@angular/common';
import { Component, OnInit, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { ICONS } from '@core/services/icons/icons.service';
import { isNil } from '@core/utils/shared-utils';

import { MultiAutocompleteComponent } from '@shared/components/autocomplete/multi-autocomplete.component';
import { ColorPickerComponent } from '@shared/components/color-picker/color-picker.component';
import { BooleanComponent } from '@shared/components/row-edition-form/boolean/boolean.component';
import { ConstComponent } from '@shared/components/row-edition-form/const/const.component';
import { CustomMapComponent } from '@shared/components/row-edition-form/custom-map/custom-map.component';
import { DateComponent } from '@shared/components/row-edition-form/date/date.component';
import { DurationComponent } from '@shared/components/row-edition-form/duration/duration.component';
import {
  FieldTaggedSection,
  FormBuilderData,
  FormBuilderFieldType,
  FormBuilderSchema,
  FormBuilderSection,
  FormGroupOptional
} from '@shared/components/row-edition-form/form-builder/form-builder.model';
import { GeojsonPolygonComponent } from '@shared/components/row-edition-form/geojson-polygon/geojson-polygon.component';
import { ItemsComponent } from '@shared/components/row-edition-form/items/items.component';
import { NumberComponent } from '@shared/components/row-edition-form/number/number.component';
import { PolylineComponent } from '@shared/components/row-edition-form/polyline/polyline.component';
import { StringComponent } from '@shared/components/row-edition-form/string/string.component';
import { TimeComponent } from '@shared/components/row-edition-form/time/time.component';
import { AvailableLanguages } from '@shared/types/translation.type';

import { TooltipComponent } from '../../tooltip/tooltip.component';
import { CompatibilityCfgFieldComponent } from '../compatibility-cfg/compatibility-cfg-field.component';
import { EntityAutoCompleteComponent } from '../entity-auto-complete/entity-auto-complete.component';
import { FormSwitchComponent } from '../form-switch/form-switch.component';
import { HiddenComponent, ValuedLoadedEvent } from '../hidden/hidden.component';
import { LabelsMapComponent } from '../labels-map/labels-map.component';
import { FormMode } from '../row-edition-form.utils';
import { TaggedSectionComponent } from '../tagged-section/tagged-section.component';
import { TagsInputComponent } from '../tags-input/tags-input.component';
import { TextListInputComponent } from '../text-list-input/text-list-input.component';

// Re-export for backward compatibility
export { getValidators } from './form-builder.utils';

@Component({
  selector: 'app-form-builder',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    NgClass,
    StringComponent,
    DateComponent,
    BooleanComponent,
    ItemsComponent,
    NumberComponent,
    TimeComponent,
    ConstComponent,
    MultiAutocompleteComponent,
    CustomMapComponent,
    DurationComponent,
    HiddenComponent,
    FormSwitchComponent,
    TooltipComponent,
    EntityAutoCompleteComponent,
    TaggedSectionComponent,
    TagsInputComponent,
    LabelsMapComponent,
    TextListInputComponent,
    GeojsonPolygonComponent,
    PolylineComponent,
    ColorPickerComponent,
    CompatibilityCfgFieldComponent
  ],
  templateUrl: './form-builder.component.html'
})
export class FormBuilderComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  protected readonly ICONS = ICONS;
  protected readonly FormBuilderFieldType = FormBuilderFieldType;
  protected readonly Array = Array;
  protected expandedSections: Record<string, boolean> = {};
  protected activatedMultiplayIndices: Record<string, number> = {};
  protected activatedMultiplayLabels: Record<string, string[]> = {};
  protected activatedMultiplayTypes: Record<
    string,
    {
      index: number;
      const?: string | null;
      wrapperProperty?: string;
      discriminatorField?: string;
      discriminatorValue?: string;
    }[]
  > = {};
  protected activatedMultiplayLoaded: Record<string, boolean> = {};
  private savedMultiplayValues: Record<string, Record<number, unknown>> = {};
  /**
   * Snapshot of each field's value taken when the form is initialised. Immutable
   * fields are locked based on this persisted value, NOT the live control value,
   * so a field the user is currently typing into (a brand-new array item) does
   * not lock mid-entry — it only locks the next time the form is opened.
   */
  private initialFieldValues: Record<string, unknown> = {};

  schema = input.required<FormBuilderSchema>();
  form = input.required<FormGroup>();
  formMode = input.required<FormMode>();
  singleColumn = input(false);
  /**
   * When true, this form-builder (and the recurrence sub-tree below it) renders
   * in a 2-track grid whose first column is three times as wide (3fr/1fr)
   * instead of the default 3-equal-column one, so the wider recurrence fields
   * (identifier, duration, recurrence) stay readable while the narrower fields
   * align at 1/4 on the right. Scoped to the recurrence configuration only;
   * every other form keeps the default 3-equal-column layout.
   */
  wideRatio = input(false);
  actualValues = input<Record<string, unknown>>();
  entityType = input<string>('');
  rowId = input<string>('');
  values: Record<string, unknown> = {};

  sectionEnabled = signal<Record<string, boolean>>({});
  constructor() {
    effect(() => {
      const values = this.actualValues();
      this.values = values ?? {};
    });
  }

  protected readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang as AvailableLanguages)
    ),
    { initialValue: this.translate.getCurrentLang() as AvailableLanguages }
  );

  ngOnInit() {
    for (const item of this.schema().items) {
      if ('type' in item) {
        this.initialFieldValues[item.id] = this.form().get(item.id)?.value;
      }

      if (!('type' in item)) {
        this.expandedSections[item.id] = false;
      }

      if ('type' in item && item.type === FormBuilderFieldType.MULTIPLAY) {
        this.activatedMultiplayIndices[item.id] = -1;
        this.activatedMultiplayLabels[item.id] = [];
        this.activatedMultiplayTypes[item.id] = [];
        const lang = this.currentLang();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        item.oneOf.forEach((oneOfItem: any, index) => {
          if (oneOfItem.type === FormBuilderFieldType.TAGGED_SECTION) {
            const tagged = oneOfItem as FieldTaggedSection;
            this.activatedMultiplayLabels[item.id].push(tagged.discriminatorValue);
            this.activatedMultiplayTypes[item.id].push({
              index,
              discriminatorField: tagged.discriminatorField,
              discriminatorValue: tagged.discriminatorValue
            });
          } else {
            this.activatedMultiplayLabels[item.id].push(
              oneOfItem.const === null
                ? this.translate.instant('common.none')
                : item.nullableDuration
                  ? this.translate.instant('form.value')
                  : (oneOfItem.const ?? oneOfItem.label?.[lang] ?? oneOfItem.id ?? '')
            );
            this.activatedMultiplayTypes[item.id].push({
              index,
              const: oneOfItem.const,
              wrapperProperty: oneOfItem.id
            });
          }
        });

        // Section-backed MULTIPLAYs (e.g. job_visits) are modelled by
        // row-edition-form as a FormGroup/FormArray envelope rather than a flat
        // FormControl, so the app-hidden value probe never runs. Resolve the
        // active variant up-front from the wrapper key present in the control.
        const control = this.form().get(item.id);
        if (control instanceof FormGroup || control instanceof FormArray) {
          const value = control.value;
          const wrapperKey =
            value && typeof value === 'object' && !Array.isArray(value)
              ? Object.keys(value)[0]
              : undefined;
          const match = this.activatedMultiplayTypes[item.id].find(
            (type) => type.wrapperProperty === wrapperKey
          );
          this.activatedMultiplayIndices[item.id] = match?.index ?? 0;
        }
      }
    }
  }

  formBuilder(
    section: FormBuilderSchema | FormBuilderSection = this.schema()
  ): (FormBuilderData | FormBuilderSection)[] {
    return section.items.sort((a, b) => a.fieldOrder - b.fieldOrder);
  }

  /**
   * Whether an immutable field must be locked (read-only) for the current item.
   *
   * Immutability protects an *already-persisted* value from being changed. The
   * decision is based on the value snapshotted at init time, not the live
   * control value, so a field that was empty when the form opened stays
   * editable for the whole session — this is what allows brand-new array items
   * (e.g. compartments created inside the trailer edit form) to have their
   * immutable `uid` filled in, while still locking once saved and reopened.
   */
  protected isImmutableLocked(fieldId: string, immutable?: boolean): boolean {
    if (!immutable || this.formMode() !== 'edit') {
      return false;
    }
    const value = this.initialFieldValues[fieldId];
    return !isNil(value) && value !== '';
  }

  inputHasError(fieldId: string): boolean {
    const control = this.form().get(fieldId);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getInputErrorCode(id: string): string {
    const control = this.form().get(id);
    if (!control || !control.errors) return '';
    return Object.keys(control.errors)[0];
  }

  getInputErrorParams(fieldId: string): object {
    const control = this.form().get(fieldId);
    if (!control || !control.errors) {
      return {};
    }

    const firstErrorKey = Object.keys(control.errors)[0];
    return control.errors[firstErrorKey];
  }

  toggleSection(sectionId: string): void {
    this.expandedSections[sectionId] = !this.expandedSections[sectionId];
  }

  isSectionExpanded(sectionId: string): boolean {
    return (
      (this.expandedSections[sectionId] ?? true) &&
      (this.sectionEnabled()[sectionId] ?? true)
    );
  }

  getFormArray(fieldId: string): FormArray {
    const control = this.form().get(fieldId);
    if (control instanceof FormArray) {
      return control;
    }
    throw new Error(`Control ${fieldId} is not a FormArray`);
  }

  /**
   * Returns the FormGroup controls backing an array section, in order.
   *
   * The @for over these must track by the control instance, not a positional
   * index: tracking by index makes Angular reuse a row's component instance for
   * a shifted neighbour when a middle item is removed, leaving write-once child
   * controls (e.g. the entity autocomplete) showing the deleted item's value.
   */
  getArrayItems(fieldId: string): FormGroup[] {
    try {
      return this.getFormArray(fieldId).controls as FormGroup[];
    } catch {
      return [];
    }
  }

  addArrayItem(event: Event, fieldId: string): void {
    event.stopPropagation();

    const formArray = this.getFormArray(fieldId);
    const section = this.schema().items.find(
      (item) => 'id' in item && item.id === fieldId
    ) as FormBuilderSection | undefined;

    if (!section || !section.items) {
      console.error(`Section ${fieldId} not found or has no items`);
      return;
    }

    const newGroup = this.createFormGroupFromSection(section);
    formArray.push(newGroup);
  }

  removeArrayItem(fieldId: string, index: number): void {
    const formArray = this.getFormArray(fieldId);
    formArray.removeAt(index);
  }

  onSectionEnableOrDisable(isActive: boolean, itemId: string): void {
    const current = this.sectionEnabled();
    current[itemId] = isActive;
    this.sectionEnabled.set(current);
  }

  private createFormGroupFromSection(section: FormBuilderSection): FormGroup {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const group: Record<string, any> = {};

    for (const item of section.items) {
      if ('type' in item) {
        const validators = [];
        if (item.required) {
          validators.push(Validators.required);
        }

        group[item.id] = new FormControl(null, validators);
      } else if ('items' in item) {
        if (item.isArray) {
          group[item.id] = new FormArray([]);
        } else {
          // Mirrors how the row form builds a loaded section: the section switch reads
          // `isRequired`, and an optional section starts off until the user turns it on.
          const sectionGroup = this.createFormGroupFromSection(item) as FormGroupOptional;
          sectionGroup.isRequired = item.required;
          if (!item.required) {
            sectionGroup.disable();
          }
          group[item.id] = sectionGroup;
        }
      }
    }

    return new FormGroup(group);
  }

  protected trackFn(item: FormBuilderData | FormBuilderSection): string {
    return item.id;
  }

  /**
   * Section ids whose sub-tree switches to the 3fr/1fr wide layout. Kept narrow
   * on purpose so only the recurrence configuration is affected; every other
   * nested form keeps the default 3-equal-column layout.
   */
  private static readonly WIDE_RATIO_SECTION_IDS = new Set(['recurrence_cfg']);

  /**
   * True when `item` is the section that introduces the wide (3fr/1fr) layout.
   * Once entered, `wideRatio` is propagated to every descendant form-builder.
   */
  protected startsWideRatio(item: FormBuilderData | FormBuilderSection): boolean {
    return FormBuilderComponent.WIDE_RATIO_SECTION_IDS.has(item.id);
  }

  protected setMultiplayIndex(id: string, i: number) {
    const control = this.form().get(id);
    if (control) {
      const schemaItem = this.schema().items.find((item) => 'id' in item && item.id === id);
      const previousIndex = this.activatedMultiplayIndices[id];
      this.activatedMultiplayIndices[id] = i;

      // A user pick, unlike TaggedSectionComponent's own composition on mount, must mark
      // the form dirty: row-edition-form.component.ts only guards a loaded-row re-patch
      // (e.g. from a live NgRx re-emission of actualValues) against clobbering unsaved
      // work when the form is dirty. An undirtied variant switch is silently reverted by
      // that guard if actualValues re-emits before the user saves.
      if (previousIndex >= 0 && previousIndex !== i) {
        control.markAsDirty();
        control.markAsTouched();
      }

      // Value save/restore only applies to flat FormControl-backed MULTIPLAYs.
      // Section-backed ones (FormGroup/FormArray) keep their nested controls.
      if (control instanceof FormControl && previousIndex >= 0 && previousIndex !== i) {
        // Save the current value for the variant we're leaving
        if (!this.savedMultiplayValues[id]) {
          this.savedMultiplayValues[id] = {};
        }
        this.savedMultiplayValues[id][previousIndex] = control.value;

        // Restore a previously saved value for the variant we're switching to, or reset
        const saved = this.savedMultiplayValues[id][i];
        control.setValue(
          saved !== undefined ? saved : this.initialVariantValue(schemaItem, i)
        );
      }

      // When "None" (null const) is selected, null is a valid value — clear validators.
      // Otherwise restore the original required validator if the field demands it.
      const selectedType = this.activatedMultiplayTypes[id]?.[i];
      if (selectedType?.const === null) {
        control.clearValidators();
      } else {
        if (schemaItem && 'required' in schemaItem && schemaItem.required) {
          control.setValidators(Validators.required);
        }
      }
      control.updateValueAndValidity();
    }
  }

  /**
   * The duration control cannot show `null`: on switching a nullable duration from None
   * it would display 0 seconds while the control still held `null`, and saving would
   * silently send None. Seed it with the 0 it displays instead.
   */
  private initialVariantValue(
    schemaItem: FormBuilderData | FormBuilderSection | undefined,
    index: number
  ): unknown {
    const isNullableDurationValue =
      !!schemaItem &&
      'type' in schemaItem &&
      schemaItem.type === FormBuilderFieldType.MULTIPLAY &&
      !!schemaItem.nullableDuration &&
      schemaItem.oneOf[index]?.type === FormBuilderFieldType.DURATION;
    return isNullableDurationValue ? 'PT0S' : null;
  }

  protected getMultiplayIndex(id: string) {
    return this.activatedMultiplayIndices[id] ?? -1;
  }

  /**
   * This is used when the enum is used to discover the index correctly to the multiplay
   * value. The value will be a constant or a subitem with a wrapper property.
   */
  protected checkMultiplayItem(data: ValuedLoadedEvent): void {
    if (this.activatedMultiplayLoaded[data.id]) {
      return;
    }
    this.activatedMultiplayLoaded[data.id] = true;
    Promise.resolve().then(() => {
      for (const item of this.schema().items) {
        if (
          'type' in item &&
          item.type === FormBuilderFieldType.MULTIPLAY &&
          item.id == data.id
        ) {
          const itemValue = data.value;
          if (isNil(itemValue)) {
            const nullVariantIndex =
              this.activatedMultiplayTypes[item.id].find((type) => type.const === null)
                ?.index ?? 0;
            this.setMultiplayIndex(item.id, nullVariantIndex);
            continue;
          }
          // if the value is an object, check if it has a wrapper property
          if (this.checkMultiplayItemAsSubItem(itemValue, item.id)) {
            continue;
          }
          // Check if it's a const
          if (this.checkMultiplayItemAsConst(itemValue, item.id)) {
            continue;
          }
          // Check if it's a plain primitive (string, number, boolean) matching a free-form variant
          if (this.checkMultiplayItemAsPrimitive(itemValue, item.id)) {
            continue;
          }
          this.setMultiplayIndex(item.id, 0);
        }
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private checkMultiplayItemAsSubItem(itemValue: any, itemId: string): boolean {
    if (!Array.isArray(itemValue) && typeof itemValue === 'object') {
      // Check for tagged section (discriminated union) match first
      const taggedMatch = this.activatedMultiplayTypes[itemId].find(
        (type) =>
          type.discriminatorField &&
          itemValue[type.discriminatorField] === type.discriminatorValue
      );
      if (taggedMatch) {
        this.setMultiplayIndex(itemId, taggedMatch.index);
        return true;
      }

      // Fallback to wrapper property match
      const propertyWrapper = Object.keys(itemValue)[0];
      const index =
        this.activatedMultiplayTypes[itemId].find(
          (type) => type.wrapperProperty === propertyWrapper
        )?.index ?? -1;
      if (index >= 0) {
        this.setMultiplayIndex(itemId, index);
        return true;
      }
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private checkMultiplayItemAsPrimitive(itemValue: any, itemId: string): boolean {
    if (itemValue === null || typeof itemValue === 'object') {
      return false;
    }
    // For a plain primitive value (string, number, boolean), find the first
    // non-const variant (one that accepts free-form input)
    const freeFormType = this.activatedMultiplayTypes[itemId].find(
      (type) => type.const === undefined
    );
    if (freeFormType) {
      this.setMultiplayIndex(itemId, freeFormType.index);
      return true;
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private checkMultiplayItemAsConst(itemValue: any, itemId: string): boolean {
    const index = this.findConstValue(itemId, itemValue);
    if (index >= 0) {
      this.setMultiplayIndex(itemId, index);
      return true;
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private findConstValue(itemId: string, itemValue: any): number {
    const arrayTypes = this.activatedMultiplayTypes[itemId].find(
      (item) => item.const === itemValue
    );
    return arrayTypes?.index ?? -1;
  }
}
